import { ErrorMessage } from '@hookform/error-message';
import { AddIcon, TrashCanSolidIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { ReactNode, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import BaseInput, { FieldLabel } from '../BaseInput';
import { SingleDateInput } from '../Inputs/DateInput';
import Label from '../Label';
export type DateRangeType = { from?: string; to?: string };

export interface IDateInputProps extends IDateField {
  disabled?: boolean;
  showYearPicker?: boolean;
  value?: string;
  minDate?: Date;
  maxDate?: Date;
  format?: 'LOCAL' | 'UTC';
}

export interface IDateRangeInputProps extends IDateRangeField {
  disbaled?: boolean;
  showYearPicker?: boolean;
  value?: DateRangeType;
  format?: 'LOCAL' | 'UTC';
  minDate?: Date;
  maxDate?: Date;
}

export interface IDateRangesInputProps extends IDateRangesField {
  disbaled?: boolean;
  showYearPicker?: boolean;
  value?: DateRangeType[];
  format?: 'LOCAL' | 'UTC';
  minDate?: Date;
  maxDate?: Date;
}

export default function DateInput(props: IDateInputProps) {
  const {
    name,
    disabled = false,
    minDate,
    maxDate,
    format = 'LOCAL',
    showYearPicker,
    ...rest
  } = props;

  return (
    <BaseInput name={name} {...rest}>
      {({ control }) => (
        <Controller
          render={({ field }) => (
            <SingleDateInput
              disabled={disabled}
              minDate={minDate}
              maxDate={maxDate}
              onChangeStringDate={(d) => field.onChange(d || null)}
              value={field.value}
              format={format}
            />
          )}
          name={name}
          control={control}
          defaultValue=""
        />
      )}
    </BaseInput>
  );
}

export function DateRangesInput(props: IDateRangesInputProps) {
  const {
    name,
    disbaled = false,
    minDate,
    maxDate,
    type,
    format = 'LOCAL',
    showYearPicker,
    effect,
    updateRemoteKey,
    label,
    ...rest
  } = props;
  const methods = useFormContext();
  const {
    formState: { errors },
    getValues,
    watch,
    control,
    setValue,
  } = methods;
  const nameWatched = watch(name);

  useEffect(
    () => {
      // if (effect && getValues(name) !== null && getValues(name) !== undefined) {
      effect && effect({ key: name, value: getValues(name) });
      // }
      if (updateRemoteKey) {
        updateRemoteKey();
      }
    },
    effect ? [nameWatched] : [],
  );

  return (
    <>
      <Controller
        render={({ field }) => (
          <_DateRangesInput
            label={<FieldLabel {...props} />}
            value={field.value}
            onChange={field.onChange}
            errors={errors[name]}
          />
        )}
        name={name}
        control={control}
        defaultValue=""
      />
      {name && errors && errors[name] && errors[name]?.type === 'min' && (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 capitalize pl-4">
              {message}
            </p>
          )}
        />
      )}
    </>
  );
}

function useStateProps<T>(value: T) {
  const [state, setState] = useState(() => value);
  useEffect(() => {
    setState(() => value);
  }, [JSON.stringify(value)]);
  return [state, setState] as const;
}

function _DateRangesInput({
  value = [],
  onChange,
  label,
  errors,
}: {
  errors?: any;
  label?: ReactNode;
  value: DateRangeType[];
  onChange: (v: DateRangeType[]) => void;
}) {
  const [dates, setDates] = useStateProps(value);
  return (
    <>
      <div className="w-full flex justify-between">
        {label}
        <Button
          className="max-w-min"
          type="button"
          onClick={() =>
            onChange((dates || []).concat({ from: undefined, to: undefined }))
          }
        >
          <AddIcon />
        </Button>
      </div>
      {(dates || []).map((date, index) => (
        <div
          key={`${index}`}
          className="flex gap-2  items-center py-2 flex-1 w-full border-gray-100"
        >
          <_DateRangeInput
            from={date.from}
            to={date.to}
            errors={errors && errors[index]}
            onChange={(d) => {
              const newDates = dates.slice();
              newDates[index] = d;
              onChange(newDates);
            }}
          />
          <Button
            className="max-w-min self-start "
            theme="red-outline"
            type="button"
            onClick={() => {
              onChange(dates.filter((d, i) => index !== i));
            }}
          >
            <TrashCanSolidIcon className="text-gray-700" />
          </Button>
        </div>
      ))}
    </>
  );
}

export function _DateRangeInput({
  from,
  to,
  minDate,
  maxDate,
  onChange,
  disabled,
  format,
  errors,
  fromName = 'from',
  toName = 'to',
}: {
  errors?: any;
  fromName?: string;
  toName?: string;
  from?: string;
  to?: string;
  minDate?: Date;
  disabled?: boolean;
  format?: 'LOCAL' | 'UTC';
  maxDate?: Date;
  onChange: (date: DateRangeType) => void;
}) {

  return (
    <div className="flex w-full gap-2">
      <div className="flex-1 flex  flex-col gap-2">
        <div className="flex flex-1 items-start gap-2">
          <Label label="From" />
          <SingleDateInput
            disabled={disabled}
            minDate={minDate}
            maxDate={to ? to : maxDate}
            onChangeStringDate={(from) => onChange({ to, from })}
            value={from}
            format={format}
          />
        </div>

        {errors && (
          <ErrorMessage
            errors={errors}
            name={fromName}
            render={({ message }) => (
              <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 capitalize pl-4">
                {message}
              </p>
            )}
          />
        )}
      </div>
      <div className="flex-1 flex  flex-col gap-2">
        <div className="flex flex-1 items-start gap-2">
          <Label label="To" />

          <SingleDateInput
            minDate={from ? from : minDate}
            maxDate={maxDate}
            value={to}
            hourOffset="max"
            onChangeStringDate={(to) => onChange({ from, to })}
          />
        </div>

        {errors && (
          <ErrorMessage
            errors={errors}
            name={toName}
            render={({ message }) => (
              <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 capitalize pl-4">
                {message}
              </p>
            )}
          />
        )}
      </div>
    </div>
  );
}
export function DateRangeInput(props: IDateRangeInputProps) {
  const {
    name,
    disbaled = false,
    minDate,
    maxDate,
    type,
    format = 'LOCAL',
    showYearPicker,
    ...rest
  } = props;

  return (
    <BaseInput name={name} {...rest}>
      {({ control }) => (
        <Controller
          render={({ field }) => (
            <div className="flex gap-2">
              <div className="flex-1">
                <Label label="From" />
                <SingleDateInput
                  disabled={disbaled}
                  minDate={minDate}
                  maxDate={field.value?.to ? field.value?.to : maxDate}
                  onChangeStringDate={(from) =>
                    field.onChange({
                      ...field.value,
                      from,
                    })
                  }
                  value={field.value?.from}
                  format={format}
                />
              </div>
              <div className="flex-1">
                <Label label="To" />

                <SingleDateInput
                  minDate={field.value?.from ? field.value?.from : minDate}
                  maxDate={new Date()}
                  value={field.value?.to}
                  hourOffset="max"
                  onChangeStringDate={(to) =>
                    field.onChange({
                      ...field.value,
                      to: to,
                    })
                  }
                />
              </div>
            </div>
          )}
          name={name}
          control={control}
          defaultValue=""
        />
      )}
    </BaseInput>
  );
}
